import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";


const NAV = [
  { to: "/about-osc", label: "About OSC" },
  { to: "/the-entrepreneur-files", label: "The Entrepreneur Files" },
  { to: "/start-a-business", label: "Start a Business" },
  { to: "/run-a-business", label: "Run a Business" },
  { to: "/blog", label: "Founder Command Blog" },
  { to: "/field-reports", label: "Declassified Field Reports" },
  { to: "/coaching-advisory", label: "Coaching & Advisory" },
  { to: "/contact", label: "Contact" },
];

// Human-readable labels for breadcrumb schema
const BREADCRUMB_LABELS = {
  "": "Home",
  "about-osc": "About OSC",
  "the-entrepreneur-files": "The Entrepreneur Files",
  "companion-workbook": "The Companion Workbook",
  "start-a-business": "Start a Business",
  "run-a-business": "Run a Business",
  "blog": "Founder Command Blog",
  "field-reports": "Declassified Field Reports",
  "business-control-diagnostic": "Business Control Diagnostic",
  "business-readiness-assessment": "Business Readiness Assessment",
  "about-marcus": "About Marcus Sanchez",
  "book-strategy-call": "Command Debrief",
  "coaching-advisory": "Coaching & Advisory",
  "control-installation": "90-Day Control Installation",
  "standing-advisory": "Standing Advisory",
  "strengthen-a-business": "Strengthen a Business",
  "apply-coaching": "Apply for Coaching",
  "contact": "Contact",
  "legal": "Legal",
  "privacy": "Privacy Policy",
  "terms": "Terms",
  "refund-policy": "Refund Policy",
  "earnings-disclaimer": "Earnings Disclaimer",
  "professional-advice": "Professional Advice",
  "copyright": "Copyright",
  "affiliate": "Affiliate Disclosure",
};

function BreadcrumbSchema({ pathname }) {
  // Only emit BreadcrumbList on non-root pages
  if (!pathname || pathname === "/") return null;
  const parts = pathname.split("/").filter(Boolean);
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://operationstrategiccodex.com/" },
  ];
  let accum = "";
  parts.forEach((seg, idx) => {
    accum += "/" + seg;
    const label = BREADCRUMB_LABELS[seg] || seg.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    items.push({
      "@type": "ListItem",
      position: idx + 2,
      name: label,
      item: `https://operationstrategiccodex.com${accum}`,
    });
  });
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-osc-black/85 backdrop-blur-xl border-b border-osc-gold/20"
    >

      <div className="max-w-[1520px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
          <ShieldCheck className="w-6 h-6 text-osc-gold" strokeWidth={1.5} />
          <div className="leading-tight">
            <div className="stamp text-[0.6rem] text-osc-gold/80">Operation Strategic Codex</div>
            <div className="font-serif text-osc-parchment text-xl tracking-tight -mt-0.5">O · S · C</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[0.78rem] uppercase tracking-widest font-mono transition-colors text-center ${
                  isActive ? "text-osc-gold" : "text-osc-gray hover:text-osc-parchment"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to="/the-entrepreneur-files" data-testid="cta-preorder-header" className="btn-gold !py-2 !px-4 !text-[0.68rem]">
            Get the E-Book $17
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-osc-gold"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-osc-gold/20 bg-osc-black">
          <div className="px-5 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-osc-parchment text-sm uppercase tracking-widest font-mono"
              >
                {n.label}
              </Link>
            ))}
            <div className="flex flex-col gap-3 pt-3 border-t border-osc-gold/15">
              <Link to="/field-reports" onClick={() => setOpen(false)} className="btn-ghost-gold">Declassified Field Reports</Link>
              <Link to="/the-entrepreneur-files" onClick={() => setOpen(false)} className="btn-gold">Get the E-Book $17</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-osc-charcoal border-t border-osc-gold/20 mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="stamp mb-3">[ Classified // Operator Channel ]</div>
          <div className="font-serif text-2xl text-osc-parchment mb-3">Operation Strategic Codex</div>
          <p className="text-osc-gray text-sm leading-relaxed max-w-md">
            Entrepreneurial intelligence for founders who build with command. Doctrine, dossiers, and field tools for starting,
            running, and strengthening a business under pressure.
          </p>
        </div>
        <div>
          <div className="stamp mb-3">Navigate</div>
          <ul className="space-y-2 text-sm text-osc-gray">
            <li><Link to="/about-osc" className="hover:text-osc-gold">About OSC</Link></li>
            <li><Link to="/the-entrepreneur-files" className="hover:text-osc-gold">The Entrepreneur Files</Link></li>
            <li><Link to="/start-a-business" className="hover:text-osc-gold">Start a Business</Link></li>
            <li><Link to="/run-a-business" className="hover:text-osc-gold">Run a Business</Link></li>
            <li><Link to="/strengthen-a-business" className="hover:text-osc-gold">Strengthen a Business</Link></li>
            <li><Link to="/blog" className="hover:text-osc-gold">Founder Command Blog</Link></li>
            <li><Link to="/field-reports" className="hover:text-osc-gold">Declassified Field Reports</Link></li>
          </ul>
        </div>
        <div>
          <div className="stamp mb-3">Command</div>
          <ul className="space-y-2 text-sm text-osc-gray">
            <li><Link to="/about-marcus" className="hover:text-osc-gold">About the Founder</Link></li>
            <li><Link to="/coaching-advisory" className="hover:text-osc-gold">Coaching & Advisory</Link></li>
            <li><Link to="/book-strategy-call" className="hover:text-osc-gold">Command Debrief</Link></li>
            <li><Link to="/apply-coaching" className="hover:text-osc-gold">Apply for Coaching</Link></li>
            <li><Link to="/contact" className="hover:text-osc-gold">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="gold-divider opacity-50" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[0.72rem] uppercase tracking-widest font-mono text-osc-gray/70">
        <div>© {new Date().getFullYear()} Operation Strategic Codex LLC. All rights reserved.</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/legal/privacy" className="hover:text-osc-gold">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-osc-gold">Terms</Link>
          <Link to="/legal/refund-policy" className="hover:text-osc-gold">Refund</Link>
          <Link to="/legal/earnings-disclaimer" className="hover:text-osc-gold">Earnings</Link>
          <Link to="/legal/professional-advice" className="hover:text-osc-gold">Advice</Link>
          <Link to="/legal/copyright" className="hover:text-osc-gold">IP</Link>
          <Link to="/legal/affiliate" className="hover:text-osc-gold">Affiliate</Link>
        </div>
      </div>
      <div className="text-center pb-6 text-[0.65rem] tracking-widest font-mono text-osc-gray/40 px-5">
        Brand identity only. No government, military, or agency affiliation.
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  // Scroll to top on route change
  React.useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-osc-black text-osc-parchment">
      <BreadcrumbSchema pathname={location.pathname} />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
